import React, { useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';

import { db } from '../../firebase';
import styles from './UpcomingEvents.module.css';

const UpcomingEvents = () => {
  const cardsRef = useRef([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch Events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const q = query(
          collection(db, 'events'),
          where('status', '==', 'published')
        );

        const querySnapshot = await getDocs(q);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const fetchedEvents = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          // Hide older events
          .filter((event) => {
            if (!event.date) return false;

            const [year, month, day] = event.date
              .split('-')
              .map(Number);

            const eventDate = new Date(year, month - 1, day);
            eventDate.setHours(0, 0, 0, 0);

            return eventDate >= today;
          })

          // Sort by nearest upcoming event
          .sort((a, b) => {
            const aDate = new Date(a.date);
            const bDate = new Date(b.date);

            return aDate - bDate;
          });

        setEvents(fetchedEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Scroll Animation
  useEffect(() => {
    if (events.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.show);
          }
        });
      },
      {
        threshold: 0.15,
      }
    );

    cardsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [events]);

  // Format Date
  const formatDate = (dateStr) => {
    if (!dateStr) return { day: '', date: '' };

    const [year, month, day] = dateStr.split('-');

    const d = new Date(year, month - 1, day);

    return {
      day: d.toLocaleDateString('en-IN', {
        weekday: 'long',
      }),
      date: d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
      }),
    };
  };

  // Format Time
  const formatTime = (timeStr) => {
    if (!timeStr) return '';

    const [hour, minute] = timeStr.split(':').map(Number);

    const period = hour >= 12 ? 'PM' : 'AM';

    const h = hour % 12 || 12;

    return `${h}:${String(minute).padStart(2, '0')} ${period}`;
  };

  // Loading State
  if (loading) {
    return (
      <div className={styles.loader}>
        Loading Events...
      </div>
    );
  }

  // No Events
  if (events.length === 0) {
    return null;
  }

  // Clear refs before render
  cardsRef.current = [];

  return (
    <section className={styles.wrapper}>
      <div className={styles.container}>
        <h2 className={styles.mainTitle}>
          Upcoming Events
        </h2>

        <div className={styles.eventList}>
          {events.map((event, index) => {
            const { day, date } = formatDate(event.date);

            const time = formatTime(event.time);

            return (
              <div
                key={event.id}
                ref={(el) => (cardsRef.current[index] = el)}
                className={styles.eventCard}
                style={{
                  transitionDelay: `${index * 150}ms`,
                }}
              >
                {/* Event Image */}
                <div className={styles.imageBox}>
                  <img
                    src={
                      event.imageUrl ||
                      'https://via.placeholder.com/400x250?text=Event'
                    }
                    alt={event.title}
                    loading="lazy"
                  />
                </div>

                {/* Event Details */}
                <div className={styles.details}>
                  <div className={styles.dateTime}>
                    {day && (
                      <span className={styles.orangeText}>
                        {day}
                      </span>
                    )}

                    {date && (
                      <span className={styles.orangeText}>
                        {date}
                      </span>
                    )}

                    {time && (
                      <span className={styles.orangeText}>
                        {time}
                      </span>
                    )}
                  </div>

                  <h3 className={styles.eventTitle}>
                    {event.title}
                  </h3>

                  {event.headline && (
                    <p className={styles.collabText}>
                      {event.headline}
                    </p>
                  )}

                  {event.hostedBy && (
                    <p className={styles.collabText}>
                      By {event.hostedBy}
                    </p>
                  )}

                  {event.price && (
                    <p className={styles.priceText}>
                      Starting @ {event.price}
                    </p>
                  )}
                </div>

                {/* CTA */}
                <div className={styles.action}>
                  {event.bookingUrl ? (
                    <a
                      href={event.bookingUrl.startsWith('http') ? event.bookingUrl : `https://${event.bookingUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.bookBtn}
                    >
                      Book Now
                    </a>
                  ) : (
                    <button
                      className={styles.bookBtn}
                      disabled
                    >
                      Coming Soon
                    </button>
                  )}
                </div>

                <span className={styles.divider}></span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UpcomingEvents;